// Resize + compress an image file to a base64 data URL. Uses createImageBitmap with
// imageOrientation 'from-image' so EXIF-rotated phone photos come out upright (no double
// rotation). Falls back to a plain <img> load (browsers auto-orient by default) if needed.
export async function fileToResizedDataUrl(file, maxDim = 2200, quality = 0.9) {
  let source;
  try {
    source = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    source = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  }
  let width = source.width || source.naturalWidth;
  let height = source.height || source.naturalHeight;
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, 0, 0, width, height);
  if (source.close) source.close();
  return canvas.toDataURL("image/jpeg", quality);
}

export function rotateDataUrl(dataUrl, degrees = 90) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const deg = ((degrees % 360) + 360) % 360;
      const rad = (deg * Math.PI) / 180;
      const swap = deg === 90 || deg === 270;
      const w = img.width;
      const h = img.height;
      const canvas = document.createElement("canvas");
      canvas.width = swap ? h : w;
      canvas.height = swap ? w : h;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -w / 2, -h / 2);
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Split an image into `parts` segments along its longer axis (keeps text lines mostly intact).
// Used to keep each transcription request small enough to finish within the gateway time limit.
export function splitImageLongAxis(dataUrl, parts = 2) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const horizontal = w >= h;
      const out = [];
      for (let i = 0; i < parts; i++) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (horizontal) {
          const seg = Math.floor(w / parts);
          const start = i * seg;
          const sw = i === parts - 1 ? w - start : seg;
          canvas.width = sw;
          canvas.height = h;
          ctx.drawImage(img, start, 0, sw, h, 0, 0, sw, h);
        } else {
          const seg = Math.floor(h / parts);
          const start = i * seg;
          const sh = i === parts - 1 ? h - start : seg;
          canvas.width = w;
          canvas.height = sh;
          ctx.drawImage(img, 0, start, w, sh, 0, 0, w, sh);
        }
        out.push(canvas.toDataURL("image/jpeg", 0.9));
      }
      resolve(out);
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}
